import type { DailyExecutionPoint } from "src/services/api/opportunities/types"
import { normalizeAdvisorDisplayName } from "src/services/api/opportunities/accountNormalize"

export interface ExecutionTimelinePoint {
    date: string
    amount_net: number
    movement_count: number
    cumulative_net: number
}

export interface AdvisorTimelineSeries {
    key: string
    label: string
    points: ExecutionTimelinePoint[]
    total_net: number
}

const UNASSIGNED_KEY = "__unassigned__"
const TOTAL_KEY = "__total__"

const toIsoDate = (raw: string | undefined): string | null => {
    if (!raw?.trim()) return null
    const s = raw.trim().slice(0, 10)
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null
}

const parseIso = (iso: string): Date => new Date(`${iso}T12:00:00`)

const addDaysIso = (iso: string, days: number): string => {
    const d = parseIso(iso)
    d.setDate(d.getDate() + days)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    return `${y}-${m}-${day}`
}

const todayIso = (): string => {
    const d = new Date()
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    return `${y}-${m}-${day}`
}

const minIso = (a: string, b: string): string => (a <= b ? a : b)

const hasAdvisorDimension = (series: DailyExecutionPoint[]): boolean =>
    series.some((p) => {
        const ia = p.investment_advisor
        if (ia && (ia.id || ia.element_id || ia.name)) return true
        return Boolean(p.advisor_id || p.advisor_element_id || p.advisor_name)
    })

const advisorKeyAndLabel = (
    p: DailyExecutionPoint,
    splitByAdvisor: boolean,
): { key: string; label: string } => {
    if (!splitByAdvisor) return { key: TOTAL_KEY, label: "" }

    const ia = p.investment_advisor
    const id = (ia?.id ?? p.advisor_id)?.toString().trim() || undefined
    const elementId = (ia?.element_id ?? p.advisor_element_id)?.toString().trim() || undefined
    const rawName = ia?.name ?? p.advisor_name
    const name = normalizeAdvisorDisplayName(rawName) || (rawName ? String(rawName).trim() : "")

    if (id) return { key: `id:${id}`, label: name || id }
    if (elementId) return { key: `eid:${elementId}`, label: name || elementId }
    if (name) return { key: `name:${name.toLocaleUpperCase("pt-BR")}`, label: name }
    return { key: UNASSIGNED_KEY, label: "" }
}

const densifySeries = (
    start: string,
    end: string,
    byDate: Map<string, { amount_net: number; movement_count: number }>,
): ExecutionTimelinePoint[] => {
    const out: ExecutionTimelinePoint[] = []
    let cumulative = 0
    let cursor = start
    const maxDays = 800
    let steps = 0
    while (cursor <= end && steps < maxDays) {
        const day = byDate.get(cursor)
        const amount_net = day?.amount_net ?? 0
        const movement_count = day?.movement_count ?? 0
        cumulative += amount_net
        out.push({
            date: cursor,
            amount_net,
            movement_count,
            cumulative_net: cumulative,
        })
        cursor = addDaysIso(cursor, 1)
        steps += 1
    }
    return out
}

/**
 * Builds one densified cumulative series per advisor that executed (total ≠ 0).
 * Falls back to a single consolidated series when the API has no advisor dimension.
 */
export const buildAdvisorExecutionTimelines = (
    createdDate: string | undefined,
    dueDate: string | undefined,
    series: DailyExecutionPoint[] | undefined,
    unassignedLabel: string,
    totalLabel: string,
): AdvisorTimelineSeries[] => {
    const start = toIsoDate(createdDate)
    const due = toIsoDate(dueDate)
    if (!start) return []

    const end = due ? minIso(todayIso(), due) : todayIso()
    if (end < start) return []

    const rows = series ?? []
    const splitByAdvisor = hasAdvisorDimension(rows)

    const byAdvisor = new Map<
        string,
        { label: string; byDate: Map<string, { amount_net: number; movement_count: number }> }
    >()

    for (const p of rows) {
        const d = toIsoDate(p.date)
        if (!d) continue
        const { key, label } = advisorKeyAndLabel(p, splitByAdvisor)
        const amt = Number.isFinite(p.amount_net) ? p.amount_net : 0
        const mc = Number.isFinite(p.movement_count) ? p.movement_count : 0
        let bucket = byAdvisor.get(key)
        if (!bucket) {
            bucket = { label, byDate: new Map() }
            byAdvisor.set(key, bucket)
        } else if (!bucket.label && label) {
            bucket.label = label
        }
        const prev = bucket.byDate.get(d)
        if (prev) {
            bucket.byDate.set(d, {
                amount_net: prev.amount_net + amt,
                movement_count: prev.movement_count + mc,
            })
        } else {
            bucket.byDate.set(d, { amount_net: amt, movement_count: mc })
        }
    }

    const advisors: AdvisorTimelineSeries[] = []
    for (const [key, bucket] of Array.from(byAdvisor.entries())) {
        const points = densifySeries(start, end, bucket.byDate)
        const total_net = points.length ? points[points.length - 1].cumulative_net : 0
        // Only advisors who executed (any non-zero cumulative volume).
        if (total_net === 0) continue
        const label =
            key === TOTAL_KEY
                ? totalLabel
                : key === UNASSIGNED_KEY
                  ? unassignedLabel
                  : bucket.label || unassignedLabel
        advisors.push({ key, label, points, total_net })
    }

    advisors.sort((a, b) => b.total_net - a.total_net || a.label.localeCompare(b.label, "pt-BR"))
    return advisors
}

/** Densified single consolidated cumulative from flat daily points. */
export const buildExecutionTimeline = (
    createdDate: string | undefined,
    dueDate: string | undefined,
    series: DailyExecutionPoint[] | undefined,
): ExecutionTimelinePoint[] => {
    const start = toIsoDate(createdDate)
    const due = toIsoDate(dueDate)
    if (!start) return []
    const end = due ? minIso(todayIso(), due) : todayIso()
    if (end < start) return []

    const byDate = new Map<string, { amount_net: number; movement_count: number }>()
    for (const p of series ?? []) {
        const d = toIsoDate(p.date)
        if (!d) continue
        const prev = byDate.get(d)
        const amt = Number.isFinite(p.amount_net) ? p.amount_net : 0
        const mc = Number.isFinite(p.movement_count) ? p.movement_count : 0
        if (prev) {
            byDate.set(d, {
                amount_net: prev.amount_net + amt,
                movement_count: prev.movement_count + mc,
            })
        } else {
            byDate.set(d, { amount_net: amt, movement_count: mc })
        }
    }
    return densifySeries(start, end, byDate)
}
